import React from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from '../hooks/useTranslation';
import { useLocation } from 'react-router-dom';
import { getImagePath } from '../utils/imagePath';

/**
 * Composant SEOHead - Gestion complète du SEO pour toutes les pages
 * Inclut : meta tags, hreflang, Open Graph, Twitter Cards, Structured Data
 */
const SEOHead = ({ 
  title, 
  description, 
  keywords,
  image = getImagePath('/assets/logo/GM.png'),
  type = 'website',
  noindex = false
}) => {
  const { t, language } = useTranslation();
  const location = useLocation();
  
  // URL de base (à adapter selon votre domaine)
  const baseUrl = typeof window !== 'undefined' ? window.location.origin : 'https://golden-mali.com';
  const currentUrl = `${baseUrl}${location.pathname}`;
  
  // Titre par défaut si non fourni
  const pageTitle = title || t('landing.title');
  const fullTitle = `${pageTitle} - ${t('landing.subtitle')}`;
  
  // Description par défaut si non fourni
  const pageDescription = description || t('landing.description');
  
  // Keywords par défaut
  const pageKeywords = keywords || 'gestion or, métaux précieux, ERP or, plateforme SaaS, Golden Mali, trading or, prix or, XAUUSD';
  
  // URLs alternates pour hreflang
  const alternateUrls = {
    fr: `${baseUrl}/fr${location.pathname}`,
    en: `${baseUrl}/en${location.pathname}`,
    'x-default': `${baseUrl}${location.pathname}`
  };
  
  // Structured Data JSON-LD
  const organizationSchema = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Golden Mali SARL",
    "url": baseUrl,
    "logo": `${baseUrl}/assets/logo/GM.png`,
    "description": t('landing.description'),
    "address": {
      "@type": "PostalAddress",
      "addressCountry": "ML"
    },
    "sameAs": []
  };
  
  const websiteSchema = {
    "@context": "https://schema.org",
    "@type": "WebSite",
    "name": "Golden Mali SARL",
    "url": baseUrl,
    "description": t('landing.description'),
    "inLanguage": [language === 'fr' ? 'fr-FR' : 'en-US'],
    "potentialAction": {
      "@type": "SearchAction",
      "target": {
        "@type": "EntryPoint",
        "urlTemplate": `${baseUrl}/search?q={search_term_string}`
      },
      "query-input": "required name=search_term_string"
    }
  };
  
  const softwareApplicationSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Gold Flow Manager",
    "applicationCategory": "BusinessApplication",
    "operatingSystem": "Web, Windows, Mac, Linux, Android, iOS",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "USD"
    },
    "description": t('landing.description')
  };

  return (
    <Helmet>
      {/* Meta tags de base */}
      <html lang={language} />
      <title>{fullTitle}</title>
      <meta name="description" content={pageDescription} />
      <meta name="keywords" content={pageKeywords} />
      <meta name="author" content="Golden Mali SARL" />
      <meta name="robots" content={noindex ? 'noindex, nofollow' : 'index, follow'} />
      <meta name="viewport" content="width=device-width, initial-scale=1.0" />
      <meta name="theme-color" content="#1a2566" />
      
      {/* Canonical URL */}
      <link rel="canonical" href={currentUrl} />
      
      {/* Balises hreflang pour SEO international */}
      <link rel="alternate" hreflang="fr" href={alternateUrls.fr} />
      <link rel="alternate" hreflang="en" href={alternateUrls.en} />
      <link rel="alternate" hreflang="x-default" href={alternateUrls['x-default']} />
      
      {/* Open Graph tags (Facebook, LinkedIn, etc.) */}
      <meta property="og:type" content={type} />
      <meta property="og:title" content={fullTitle} />
      <meta property="og:description" content={pageDescription} />
      <meta property="og:image" content={`${baseUrl}${image}`} />
      <meta property="og:url" content={currentUrl} />
      <meta property="og:site_name" content="Golden Mali SARL" />
      <meta property="og:locale" content={language === 'fr' ? 'fr_FR' : 'en_US'} />
      <meta property="og:locale:alternate" content={language === 'fr' ? 'en_US' : 'fr_FR'} />
      
      {/* Twitter Card tags */}
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={fullTitle} />
      <meta name="twitter:description" content={pageDescription} />
      <meta name="twitter:image" content={`${baseUrl}${image}`} />
      <meta name="twitter:site" content="@GoldenMali" />
      
      {/* Structured Data JSON-LD */}
      <script type="application/ld+json">
        {JSON.stringify(organizationSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(websiteSchema)}
      </script>
      <script type="application/ld+json">
        {JSON.stringify(softwareApplicationSchema)}
      </script>
    </Helmet>
  );
};

export default SEOHead;
